import React, { useState, FormEvent, useEffect } from 'react';
import InputField from './InputField';
import TextArea from './TextArea';
import ContactInfo from './ContactInfo';
import Image from './Image';
import imgForm from '../../shared/assets/img/Frame 14.png';

interface FormData {
  name: string;
  phone: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '+',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState('');

  // Автоматическое скрытие сообщения об успехе через 8 секунд
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 8000); // 8 секунд

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const validateField = (name: keyof FormData, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) {
          return 'Name is required';
        }
        if (value.trim().length < 2) {
          return 'Name must be at least 2 characters';
        }
        break;
      case 'phone':
        if (!value || value === '+') {
          return 'Phone is required';
        }
        if (value.length < 10) {
          return 'Phone must be at least 10 digits';
        }
        break;
      case 'email':
        if (!value.trim()) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'subject':
        if (!value.trim()) {
          return 'Subject is required';
        }
        if (value.trim().length < 3) {
          return 'Subject must be at least 3 characters';
        }
        break;
      case 'message':
        if (!value.trim()) {
          return 'Message is required';
        }
        if (value.trim().length < 10) {
          return 'Message must be at least 10 characters';
        }
        break;
    }
    return '';
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBlur = (field: keyof FormData) => {
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error || undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof FormData>).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Полностью очищаем все поля формы
      setFormData({
        name: '',
        phone: '+',
        email: '',
        subject: '',
        message: '',
      });
      setErrors({});
      setSuccessMessage('Your message has been sent successfully!');
    } else {
      setSuccessMessage('');
    }
  };

  return (
    <>
      <h1 className="self-start my-20 mb-0 text-6xl font-bold text-black max-md:text-5xl max-sm:text-4xl max-w-[1290px] mx-auto" id="contacts">Get in touch</h1>
      <div className="flex gap-[100px] py-0 mx-auto my-[50px] max-w-[1290px] max-md:flex-col max-sm:p-2.5">
        <div className="w-[34%] max-md:w-full">
          <Image src={imgForm} alt="Ice fishing" className="object-cover w-full h-auto rounded-3xl aspect-[0.71] bg-zinc-200" />
        </div>
        <div className="pt-3.5 w-[66%] max-md:w-full">
          <form className="flex gap-5 mb-12 max-md:flex-col" onSubmit={handleSubmit}>
            <div className="flex-1">
              <InputField 
                label="Your Name" 
                type="text" 
                value={formData.name}
                onChange={(value) => handleChange('name', value)}
                onBlur={() => handleBlur('name')}
                error={errors.name}
                required
              />
              <InputField 
                label="Your Phone" 
                type="tel" 
                addPrefix={true}
                value={formData.phone}
                onChange={(value) => handleChange('phone', value)}
                onBlur={() => handleBlur('phone')}
                error={errors.phone}
                required
              />
              <TextArea 
                label="Your Message" 
                placeholder="Enter your message here"
                value={formData.message}
                onChange={(value) => handleChange('message', value)}
                onBlur={() => handleBlur('message')}
                error={errors.message}
                required
              />
            </div>
            <div className="flex-1">
              <InputField 
                label="Your Email" 
                type="email" 
                placeholder="example@example.com" 
                validateEmail={true}
                value={formData.email}
                onChange={(value) => handleChange('email', value)}
                onBlur={() => handleBlur('email')}
                error={errors.email}
                required
              />
              <InputField 
                label="Your Subject" 
                type="text"
                value={formData.subject}
                onChange={(value) => handleChange('subject', value)}
                onBlur={() => handleBlur('subject')}
                error={errors.subject}
                required
              />
            </div>
            <button type="submit" className="flex gap-2.5 items-center px-8 py-5 text-xl font-bold bg-yellow-400 rounded-3xl cursor-pointer border-[none] max-sm:px-5 max-sm:py-4 justify-center">
              <span>Send</span>
            </button>
          </form>
          {successMessage && <p className="text-green-600 font-bold">{successMessage}</p>}
          <div className="flex gap-12 mt-12 max-md:flex-col max-md:gap-5 max-sm:mt-8">
            <ContactInfo email="info@huter.diy" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;
